import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import { FormattedMoney } from '@repo/design-system-kit';
import { CURRENCIES } from 'receivable-invoices-js/react/constants/currencies';
import { UNITS } from 'receivable-invoices-js/react/constants/units';
import type { Item as ItemType } from 'receivable-invoices-js/react/api/models/section';
import styles from './item.module.css';

interface ItemProps extends ComponentPropsWithoutRef<'tr'> {
  item: ItemType;
}

export function Item({ item, className, ...props }: ItemProps): ReactNode {
  const { formatMessage } = useIntl();

  const unitPrice = item.unitPrice.value;
  const currency = item.unitPrice.currency || CURRENCIES.default;

  const getUnit = (): string => {
    if (UNITS.includes(item.unit)) {
      return formatMessage({ id: `unit.${item.unit}` }, { count: Math.abs(Number(item.quantity)) });
    }
    return item.unit || '';
  };

  return (
    <tr className={cx(styles.row, className)} data-testid="item-row" {...props}>
      <td className={cx(styles.cell, styles.left)}>
        <p className={styles.title} data-testid="item-title">
          {item.title}
        </p>
        <p className={styles.details} data-testid="item-description">
          {item.description}
        </p>
      </td>
      <td className={cx(styles.cell, styles.right)}>
        <p className={styles.title} data-testid="item-price">
          <FormattedMoney amount={unitPrice} currency={currency} token="money-symbol" />
        </p>
        <p className={cx(styles.details, styles.quantity)} data-testid="item-quantity">
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx -- Colon is a separator, not a translatable string */}
          {formatMessage({ id: 'invoice.preview.quantity' })}: {item.quantity} {getUnit()}
        </p>
      </td>
    </tr>
  );
}
